import { Button, Card, Col, Descriptions, Row, Select, Spin, Typography } from "antd";
import { useEffect, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import swal from "sweetalert";
import useUpdateQueue from "../../../../Hooks/mutation/useUpdateQueue";
import useGetQueueById from "../../../../Hooks/queries/useGetQueueById";
import useGetQueueStatus from "../../../../Hooks/queries/useGetQueueStatus";
import "./DetailQueue.css";
import React from 'react'

const {  Text } = Typography;

const DetailQueue = () => {
  const { value } = useParams();
  const history = useHistory();
  const queueId = parseInt(value);

  const containerRef = useRef(null);
  const [footerLeft, setFooterLeft] = useState(250);

  useEffect(() => {
    const updateLayout = () => {
      if (containerRef.current) {
        setFooterLeft(containerRef.current.getBoundingClientRect().left);
      }
    };
    updateLayout();
    window.addEventListener('resize', updateLayout);
    
    let observer;
    if (window.ResizeObserver && containerRef.current) {
      observer = new ResizeObserver(updateLayout);
      observer.observe(containerRef.current);
    }
    return () => {
      window.removeEventListener('resize', updateLayout);
      if (observer) observer.disconnect();
    };
  }, []);

  const [selectedStatus, setSelectedStatus] = useState(null);

  // Queries
  const { data: queueData, isLoading: isLoadingQueue } = useGetQueueById(queueId);
  const { data: statusData, isLoading: isLoadingStatus } = useGetQueueStatus();

  // Mutation
  const { mutate: updateQueue, isLoading: isUpdating } = useUpdateQueue();

  // Extract data safely with optional chaining
  const queueInfo = queueData?.queue_info;
  const child = queueInfo?.child;
  const parents = child?.parents;
  const vaccinations = queueInfo?.vaccinations || [];
  
  // Format Date
  const rawDate = queueInfo?.date ? new Date(queueInfo.date) : null;
  const formattedDate = rawDate 
    ? `${String(rawDate.getDate()).padStart(2, '0')}-${String(rawDate.getMonth() + 1).padStart(2, '0')}-${rawDate.getFullYear()}`
    : "-";

  const queueStatuses = statusData?.queueStatuses
    ?.filter((item) => ![10, 11, 12].includes(item.id))
    ?.map((status) => ({
      value: status.id,
      label: status.name,
    })) || [];

  // Set initial status when data loads
  useEffect(() => {
    if (queueInfo?.queue_status) {
      setSelectedStatus(queueInfo.queue_status.id);
    }
  }, [queueInfo?.queue_status]);

  const handleStatusChange = (val) => {
    setSelectedStatus(val);
  };

  const handleSave = () => {
    if (!selectedStatus) return;

    const payload = {
      _method: "PATCH",
      queue_status_id: selectedStatus,
    };

    updateQueue(
      { queueId, payload },
      {
        onSuccess: () => {
          swal({
            title: "Sukses!",
            text: "Data Antrian Telah Berhasil Diubah!",
            icon: "success",
            button: "OK",
          }).then((move) => {
            if (move) {
              history.goBack();
            }
          });
        },
        onError: () => {
          swal({
            title: "GAGAL",
            text: "Terjadi kesalahan saat menyimpan data",
            icon: "warning",
            type: "error",
            buttons: "OK",
          });
        },
      }
    );
  };

  const isPageLoading = isLoadingQueue || isLoadingStatus || isUpdating;

  return (
    <Spin spinning={isPageLoading} tip="Memuat data..." size="large">
      <div className="content-section" ref={containerRef}>
        <div className="title-section mb-5">
          <h1 className="patient-page-title">Detail Antrian</h1>
          <h2 className="patient-page-subtitle">
            <span style={{ color: "#039BEB" }}>Manajemen Antrian</span>
            <span style={{ color: "#039BEB" }}> / Daftar Antrian</span>
            <span style={{ color: "#00000029" }}> / Detail Antrian</span>
          </h2>
        </div>

        <Card className="shadow mb-5" bordered={false} style={{ textAlign: 'left' }}>
          <Row gutter={[24, 24]}>
            {/* Kolom 1: Orang Tua */}
            <Col xs={24} lg={8}>
              <Descriptions title="Informasi Orang Tua" bordered column={1} className="mb-4" size="small" labelStyle={{ width: '40%' }}>
                <Descriptions.Item label="Nama Ayah">{parents?.father_name || "-"}</Descriptions.Item>
                <Descriptions.Item label="Nama Ibu">{parents?.mother_name || "-"}</Descriptions.Item>
                <Descriptions.Item label="Alamat">{parents?.address || "-"}</Descriptions.Item>
                <Descriptions.Item label="No. Telp">{parents?.phone_number || "-"}</Descriptions.Item>
              </Descriptions>
            </Col>

            {/* Kolom 2: Anak */}
            <Col xs={24} lg={8}>
              <Descriptions title="Informasi Anak" bordered column={1} className="mb-4" size="small" labelStyle={{ width: '40%' }}>
                <Descriptions.Item label="Nama">{child?.fullname || "-"}</Descriptions.Item>
                <Descriptions.Item label="Umur">{child?.age !== null && child?.age !== undefined ? `${child.age} tahun` : "-"}</Descriptions.Item>
                <Descriptions.Item label="Jenis Kelamin">
                  {child?.gender === "male" ? "Laki-laki" : child?.gender === "female" ? "Perempuan" : "-"}
                </Descriptions.Item>
              </Descriptions>
            </Col>

            {/* Kolom 3: Waktu */}
            <Col xs={24} lg={8}>
              <Descriptions title="Informasi Waktu" bordered column={1} className="mb-4" size="small" labelStyle={{ width: '40%' }}>
                <Descriptions.Item label="Tanggal Antri">{formattedDate}</Descriptions.Item>
                <Descriptions.Item label="No. Antrian">{queueInfo?.registration_number || "-"}</Descriptions.Item>
              </Descriptions>
            </Col>
          </Row>

          <Row gutter={[24, 24]}>
            {/* Full width: Informasi Vaksin */}
            <Col xs={24}>
              <div className="mb-4">
                <div className="ant-descriptions-header">
                  <div className="ant-descriptions-title">Informasi Vaksin</div>
                </div>
                {vaccinations.length > 0 ? (
                  vaccinations.map((item, index) => (
                    <Card type="inner" title={`Vaksin ${index + 1}`} key={index} className="mb-3" size="small">
                      <Descriptions bordered column={1} className="mb-3" size="small" labelStyle={{ width: '20%' }}>
                        <Descriptions.Item label="Merek Vaksin">{item?.vaccine?.brand_name || "-"}</Descriptions.Item>
                        <Descriptions.Item label="Batch">{item?.batch || "-"}</Descriptions.Item>
                      </Descriptions>

                      {item?.vaccine_types?.length > 0 && (
                        <>
                          <Text strong>Tipe Vaksin:</Text>
                          {item.vaccine_types.map((type, tIndex) => (
                            <Descriptions
                              key={tIndex}
                              bordered
                              size="small"
                              column={1}
                              className="mt-2 mb-2"
                              labelStyle={{ width: '20%' }}
                            >
                              <Descriptions.Item label="Nama">
                                {type?.full_name}{' '}
                                {type?.name && type?.full_name ? `- ${type.name}` : type?.name}
                              </Descriptions.Item>
                              <Descriptions.Item label="Periode">{type?.pivot?.period || "-"}</Descriptions.Item>
                              <Descriptions.Item label="Frekuensi">{type?.frequency || "-"}</Descriptions.Item>
                            </Descriptions>
                          ))}
                        </>
                      )}
                    </Card>
                  ))
                ) : (
                  <Text type="secondary">Tidak ada data vaksinasi</Text>
                )}
              </div>
            </Col>
          </Row>
        </Card>

        {/* Action Bar (Fixed Footer) */}
        <div 
          className="action-bar-footer"
          style={{ left: footerLeft - 24 }}
        >
          <div className="input-detail-queue d-flex align-items-center mb-0">
            <Text strong className="mr-3" style={{ minWidth: '120px' }}>Status Antrian :</Text>
            <Select
              style={{ width: 250 }}
              options={queueStatuses}
              value={selectedStatus}
              onChange={handleStatusChange}
              placeholder="Pilih Status Antrian"
            />
          </div>
          
          <div className="patient-buttons m-0">
            <Button 
              danger 
              onClick={() => history.goBack()}
              size="large"
            >
              Batal
            </Button>
            <Button 
              type="primary" 
              onClick={handleSave} 
              disabled={!selectedStatus}
              size="large"
            >
              Simpan
            </Button>
          </div>
        </div>
      </div>
    </Spin>
  );
};

export default DetailQueue;
